import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit, Trash2, AlertCircle, X, Check, Users, Package, FileText, LogOut, BarChart3, Ship, Layers } from 'lucide-react';
import clientService from '../services/clientService';
import { authService } from '../services/authService';

function Clientes() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    rfc: '',
    direccion: '',
    telefono: '',
    email: ''
  });
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadClientes();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadClientes = async () => {
  try {
    setLoading(true);
    setError('');
    const data = await clientService.getClientes();
    
    // Si es cliente, solo mostrar su propio cliente
    if (user?.rol === 'cliente') {
      const miCliente = data.filter(c => c.id === user.cliente_id);
      setClientes(miCliente);
    } else {
      setClientes(data);
    }
  } catch (err) {
    setError(err);
  } finally {
    setLoading(false);
  }
};

  const handleOpenModal = (mode, cliente = null) => {
    setModalMode(mode);
    setSelectedCliente(cliente);
    
    if (mode === 'create') {
      setFormData({
        nombre: '',
        rfc: '',
        direccion: '',
        telefono: '',
        email: ''
      });
    } else if (mode === 'edit' && cliente) {
      setFormData({
        nombre: cliente.nombre,
        rfc: cliente.rfc || '',
        direccion: cliente.direccion || '',
        telefono: cliente.telefono || '',
        email: cliente.email || ''
      });
    }
    
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCliente(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (modalMode === 'create') {
        await clientService.createCliente(formData);
        setSuccess('Cliente creado exitosamente');
      } else if (modalMode === 'edit') {
        await clientService.updateCliente(selectedCliente.id, formData);
        setSuccess('Cliente actualizado exitosamente');
      }
      
      await loadClientes();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setError(err);
    }
  };

  const handleDelete = async (clienteId) => {
    if (!window.confirm('¿Estás seguro de desactivar este cliente?')) {
      return;
    }

    try {
      setError('');
      await clientService.deleteCliente(clienteId);
      setSuccess('Cliente desactivado exitosamente');
      await loadClientes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        {/* Logo */}
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>

          <Link
            to="/equipos"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Package size={20} />
            <span>Equipos</span>
          </Link>

          <Link
            to="/tipos-equipo"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>

          <Link
            to="/clientes"
            className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg"
          >
            <Building2 size={20} />
            <span>Clientes</span>
          </Link>

          {user?.rol === 'admin' && (
            <Link
              to="/users"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
            >
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}

          <Link
            to="/solicitudes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <FileText size={20} />
            <span>Solicitudes</span>
          </Link>

          <Link
            to="/informes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-blue-800 rounded-lg transition"
              title="Cerrar Sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white shadow-sm p-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">Gestión de Clientes</h1>
        {user?.rol === 'admin' && (
          <button
            onClick={() => handleOpenModal('create')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            Crear Cliente
          </button>
        )}
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">Cargando clientes...</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      RFC
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clientes.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No hay clientes registrados
                      </td>
                    </tr>
                  ) : (
                    clientes.map((cliente) => (
                      <tr key={cliente.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Building2 className="text-blue-500 mr-3" size={20} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{cliente.nombre}</div>
                              <div className="text-sm text-gray-500">{cliente.direccion}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {cliente.rfc || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{cliente.email || '-'}</div>
                          <div className="text-sm text-gray-500">{cliente.telefono || '-'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            cliente.activo
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {cliente.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                          {user?.rol === 'admin' && (
                            <>
                              <button
                                onClick={() => handleOpenModal('edit', cliente)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Editar"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDelete(cliente.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Desactivar"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {modalMode === 'create' ? 'Crear Cliente' : 'Editar Cliente'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RFC
                </label>
                <input
                  type="text"
                  name="rfc"
                  value={formData.rfc}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  {modalMode === 'create' ? 'Crear' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clientes;